import React from 'react';
import { Button } from '@heroui/react';
import { IDataField, IDataOption, TFrequencyType } from '../../types/reportsTypes';
import CheckboxGroup from './components/CheckboxGroup';
import DataFrequency from './components/DataFrequency';

interface DataSelectionProps {
	dataFields: IDataField[];
	onDataFieldsChange: (fields: IDataField[]) => void;
	dataOptions?: IDataOption;
	onDataOptionsChange?: (options: IDataOption) => void;
	frequencyOptions?: { key: TFrequencyType; label: string }[]; // Optional prop for frequency options
}

export const DataSelection: React.FC<DataSelectionProps> = ({
	dataFields,
	onDataFieldsChange,
	dataOptions,
	onDataOptionsChange,
	frequencyOptions,
}) => {
	// Check if any data fields are selected
	const isLoadDisabled = !dataFields.some((field) => field.selected) //|| !dataOptions?.frequency;

	// Helper function to format category names for display
	const formatCategoryName = (category: string): string => {
		const categoryMap: { [key: string]: string } = {
			position: 'Position Data',
			'emissions-ttw': 'Emissions (Tank to Wake)',
			'emissions-wtw': 'Emissions (Well to Wake)',
			fuel: 'Fuel Consumption',
		};

		// Return mapped name if exists, otherwise format the category string
		return (
			categoryMap[category] ||
			category
				.split('-')
				.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
				.join(' ')
		);
	};

	// Get unique categories from dataFields and order them by groupOrder if available, otherwise alphabetically
	const categories = Array.from(
		new Set(dataFields.map((field) => field.category).filter((category) => category !== undefined && category !== null)),
	).sort((a, b) => {
		const aField = dataFields.find((field) => field.category === a);
		const bField = dataFields.find((field) => field.category === b);
		const aOrder = aField?.groupOrder ?? 0;
		const bOrder = bField?.groupOrder ?? 0;
		return aOrder - bOrder || a.localeCompare(b);
	});

	return (
		<div className="flex flex-col h-full gap-2 space-y-4">
			<h2 className="text-xl font-semibold text-msi-midblue-color-500 border-b">Data Selection</h2>
			<div className="w-full h-full flex flex-col overflow-auto">
				<div className="h-full  overflow-x-hidden overflow-y-auto mb-5">
					<div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-x-6">
						{categories.map((category) => (
							<div key={category} className="mb-2 min-w-0">
								<h3 className="font-semibold mb-2 text-msi-midblue-color-500">{formatCategoryName(category)}</h3>
								<CheckboxGroup fields={dataFields} onChange={onDataFieldsChange} category={category} aria-label={`Select ${formatCategoryName(category)}`} />
							</div>
						))}
					</div>
				</div>
				{dataOptions && (
					<div className="h-1/4 mt-5 space-y-4">
						<div className="space-y-4 w-full">
							<h3 className="text-xl font-semibold text-msi-midblue-color-500 border-b">Additional Options</h3>
							<DataFrequency
								value={dataOptions.frequency}
								onChange={(frequency) => onDataOptionsChange!({ ...dataOptions, frequency })}
								frequencyOptions={frequencyOptions}
							/>
						</div>
					</div>
				)}
				<div className="flex justify-end">
					<Button
						className="rounded"
						color="primary"
						variant="solid"
						onPress={() => window.dispatchEvent(new CustomEvent('activity-report:load'))}
						size="sm"
						isDisabled={isLoadDisabled}
					>
						Load
					</Button>
				</div>
			</div>
		</div>
	);
};
